import { X, ExternalLink, Award, ShieldCheck, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { LookupResultItem } from '../types';
import api from '../lib/axios';

interface Props {
  domains: LookupResultItem[];
  onRemove: (domain: string) => void;
  onClose: () => void;
}

export default function CompareDrawer({ domains, onRemove, onClose }: Props) {
  if (domains.length === 0) return null;

  const fmt = (n: number | null) => {
    if (n === null || n === undefined) return '—';
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  const bestValue = (field: string) => {
    const values = domains.map(d => {
      switch (field) {
        case 'price': return d.min_price;
        case 'dr': return d.dr;
        case 'da': return d.da;
        case 'traffic': return d.organic_traffic;
        case 'sellers': return d.seller_count;
        default: return null;
      }
    });
    if (field === 'price') {
      const valid = values.filter(v => v !== null) as number[];
      return valid.length > 0 ? Math.min(...valid) : null;
    }
    const valid = values.filter(v => v !== null) as number[];
    return valid.length > 0 ? Math.max(...valid) : null;
  };

  const isBest = (value: number | null, field: string) => {
    if (value === null) return false;
    const best = bestValue(field);
    if (best === null) return false;
    if (field === 'price') return value === best;
    return value === best;
  };

  const metrics = [
    { label: 'Best Price', field: 'price', format: (d: LookupResultItem) => d.min_price !== null ? `$${d.min_price.toFixed(0)}` : '—' },
    { label: 'DR', field: 'dr', format: (d: LookupResultItem) => d.dr?.toString() ?? '—' },
    { label: 'DA', field: 'da', format: (d: LookupResultItem) => d.da?.toString() ?? '—' },
    { label: 'TF', field: 'tf', format: (d: LookupResultItem) => d.tf?.toString() ?? '—' },
    { label: 'Traffic', field: 'traffic', format: (d: LookupResultItem) => fmt(d.organic_traffic) },
    { label: 'Ref Domains', field: 'refs', format: (d: LookupResultItem) => fmt(d.ref_domains) },
    { label: 'Backlinks', field: 'backlinks', format: (d: LookupResultItem) => fmt(d.backlinks) },
    { label: 'Sellers', field: 'sellers', format: (d: LookupResultItem) => d.seller_count.toString() },
    { label: 'Country', field: 'country', format: (d: LookupResultItem) => d.country || '—' },
    { label: 'Category', field: 'category', format: (d: LookupResultItem) => d.category || '—' },
  ];

  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-[480px] bg-white border-l border-slate-200 shadow-2xl z-50 flex flex-col">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
        <div>
          <h2 className="font-bold text-slate-900">Compare Domains</h2>
          <p className="text-xs text-slate-500">{domains.length} domain{domains.length !== 1 ? 's' : ''} selected</p>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
          <X className="w-5 h-5 text-slate-500" />
        </button>
      </div>

      {/* Comparison Content */}
      <div className="flex-1 overflow-y-auto p-5">
        {/* Domain Headers */}
        <div className="grid gap-3 mb-6" style={{ gridTemplateColumns: `repeat(${domains.length}, 1fr)` }}>
          {domains.map(d => (
            <div key={d.domain} className="relative bg-slate-50 rounded-xl p-3 text-center">
              <button onClick={() => onRemove(d.domain)} className="absolute top-1 right-1 p-1 hover:bg-slate-200 rounded">
                <X className="w-3 h-3 text-slate-400" />
              </button>
              <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-sm font-bold text-slate-500 uppercase mx-auto mb-2">
                {d.domain.charAt(0)}
              </div>
              <Link to={`/domain/${d.domain}`} className="text-sm font-semibold text-slate-900 hover:text-brand-600 block truncate">{d.domain}</Link>
              {d.category && <p className="text-xs text-slate-400 mt-0.5">{d.category}</p>}
            </div>
          ))}
        </div>

        {/* Metrics Comparison */}
        <div className="space-y-0">
          {metrics.map(metric => (
            <div key={metric.label} className="border-b border-slate-100 py-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{metric.label}</p>
              <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${domains.length}, 1fr)` }}>
                {domains.map(d => {
                  const value = metric.format(d);
                  const rawValue = metric.field === 'price' ? d.min_price : metric.field === 'dr' ? d.dr : metric.field === 'da' ? d.da : metric.field === 'traffic' ? d.organic_traffic : metric.field === 'sellers' ? d.seller_count : null;
                  const best = rawValue !== null && isBest(rawValue, metric.field);
                  return (
                    <div key={d.domain} className={`text-center py-2 rounded-lg ${best ? 'bg-emerald-50 text-emerald-700 font-bold' : 'text-slate-700'}`}>
                      <span className="text-sm font-semibold">{value}</span>
                      {best && metric.field === 'price' && <span className="block text-xs text-emerald-600 mt-0.5">Cheapest</span>}
                      {best && metric.field === 'dr' && <span className="block text-xs text-emerald-600 mt-0.5">Highest</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Seller Breakdown */}
        <div className="mt-6">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Cheapest Seller</p>
          <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${domains.length}, 1fr)` }}>
            {domains.map(d => {
              const cheapest = d.prices.length > 0 ? d.prices.sort((a, b) => a.price - b.price)[0] : null;
              return (
                <div key={d.domain} className="bg-slate-50 rounded-xl p-3 text-center">
                  {cheapest ? (
                    <>
                      <p className="text-sm font-semibold text-slate-900">{cheapest.seller_name}</p>
                      <p className="text-lg font-bold text-emerald-600">${cheapest.price.toFixed(0)}</p>
                      {cheapest.seller_website && (
                        <button
                          onClick={() => {
                            api.post('/api/clicks/track', null, { params: { seller_id: cheapest.seller_id, domain: d.domain } }).catch(() => {});
                            window.open(cheapest.seller_website!, '_blank');
                          }}
                          className="mt-2 w-full py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold rounded-lg flex items-center justify-center gap-1"
                        >
                          View <ExternalLink className="w-3 h-3" />
                        </button>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-slate-400">No sellers</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-4 border-t border-slate-200 flex-shrink-0">
        <button onClick={onClose} className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-colors">
          Close Comparison
        </button>
      </div>
    </div>
  );
}
